import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X } from 'lucide-react';
import { GeneratedCover } from '../components/ui/GeneratedCover';
import { listBooks } from '../services/api';

export const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('q') || '';
  const [query, setQuery] = useState(initial);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listBooks(query ? { q: query } : undefined);
        setResults(res.books || []);
        setSearchParams(q => {
          if (query) q.set('q', query); else q.delete('q');
          return q;
        });
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [query, setSearchParams]);

  return (
    <div className="pb-24 px-6 pt-6 min-h-screen">
      <h1 className="sr-only">Search</h1>
      
      {/* Search Input */}
      <div className="relative mb-8">
        <input
          type="text"
          placeholder="Look for a title, a name, a place, or a problem."
          className="w-full bg-black/5 dark:bg-white/10 rounded-xl py-3 pl-12 pr-4 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="absolute left-4 top-3.5 opacity-50 pointer-events-none">
          <SearchIcon size={20} />
        </div>
        {query && (
          <button 
            onClick={() => setQuery('')}
            className="absolute right-3 top-3 p-1 rounded-full bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg.white/20"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Results */}
      <div>
        {loading && <div className="text-sm opacity-60">Searching...</div>}
        {error && <div className="text-sm text-red-500">{error}</div>}
        {!loading && !error && (
          <>
            {query ? (
              <h2 className="text-xs font-bold uppercase tracking-wider opacity-50 mb-4">Results</h2>
            ) : (
              <h2 className="text-xs font-bold uppercase tracking-wider opacity-50 mb-4">
                All Case Files
              </h2>
            )}

            <div className="space-y-4">
              {results.map(book => (
                <Link key={book.id} to={`/book/${book.id}`} className="flex gap-4 group">
                  <GeneratedCover title={book.title} author={book.author} genre={book.genre} size="sm" className="shrink-0 h-24 w-16 shadow-sm" />
                  <div className="py-1">
                    <h3 className="font-serif font-bold text-base mb-1 group-hover:text-brand-blue">{book.title}</h3>
                    <p className="text-sm opacity-70 mb-2">{book.author}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-black/5 dark:bg-white/10 px-2 py-0.5 rounded text-opacity-70">{book.genre}</span>
                      {book.is_new && <span className="text-[10px] text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded font-medium">New</span>}
                    </div>
                  </div>
                </Link>
              ))}
              {results.length === 0 && (
                <div className="text-center py-12 opacity-50">
                  <p className="font-semibold">No case files match your search.</p>
                  <p className="text-sm mt-2 leading-relaxed">
                    Maybe:<br />
                    – the story hasn’t been written yet,<br />
                    – it’s buried in someone’s archive,<br />
                    – or the spelling needs another try.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
